import { useEffect, useState, useCallback, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Pagination, Skeleton } from "@mui/material";
import { TopBarContext } from "../../providers/TopBarProvider";
import OrderItem from "../molecules/OrderItem";
import CustomNoRows from "../atoms/CustomNoRows";
import OrderService from "../../services/OrderService";
import OrderClient from "../molecules/OrderClient";
import { ToastContext } from "../../providers/ToastProvider";
import { UserInfoContext } from "../../providers/UserInfoProvider";

const Products = () => {
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mySearch, setMySearch] = useState("");
  const [orderClient, setOrderClient] = useState(false);
  const { setTitle, setSearchOptions, setBtnPrimary } =
    useContext(TopBarContext);
  const { pers } = useContext(UserInfoContext);
  const { setShowToast, setToastMessage } = useContext(ToastContext);
  const orderService = useMemo(() => new OrderService(), []);
  const rowsPerPage = 20;
  const navigate = useNavigate();

  const getAll = useCallback(
    async (skipCount = 0, search) => {
      try {
        setIsLoading(true);
        setOrders([]);
        const res = await orderService.getAll(
          rowsPerPage,
          skipCount * rowsPerPage,
          search
        );
        const totalCount = res.data.result.totalCount;
        const items = res.data.result.items;

        if (items.length === 0) {
          return;
        }

        setCount(Math.ceil(totalCount / rowsPerPage));
        setOrders([...items]);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [orderService, setOrders]
  );

  const onPagination = (_, value) => {
    setOrders([]);
    setPage(0);
    getAll(value - 1, mySearch);
    window.scrollTo(0, 0);
  };

  const handleSuspend = (orderId) => {
    return new Promise(async (resolve, reject) => {
      try {
        await orderService.suspend(orderId);
        resolve();
      } catch {
        setToastMessage([
          {
            severity: "error",
            primary: `Error al intentar suspender la orden #${orderId}`,
          },
        ]);
        setShowToast(true);

        reject();
      }
    });
  };

  useEffect(() => {
    setTitle("Órdenes");
    setSearchOptions({
      label: "Buscar orden por cliente",
      action: (search) => {
        setMySearch(search);
        getAll(0, search);
      },
    });
    setBtnPrimary({
      label: "Agregar orden",
      action: () => setOrderClient(true),
      icon: "plus",
      disabled: !pers.includes("pages.orders.create"),
    });
  }, [setTitle, setSearchOptions, setBtnPrimary, getAll, pers]);

  useEffect(() => {
    getAll();
  }, [getAll]);

  const classes = {
    root: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, 335px)",
      gridGap: 20,
      justifyContent: "center",
      my: 8,
    },
    skeleton: {
      width: "100%",
      height: 311,
    },
    customNoRows: {
      gridColumn: "6 span",
    },
    pagination: {
      width: "fit-content",
      margin: "0 auto",
    },
  };

  return (
    <>
      <Box sx={classes.root}>
        {isLoading ? (
          new Array(rowsPerPage)
            .fill(1)
            .map((_, idx) => (
              <Skeleton
                animation="wave"
                key={`skeleton-${idx}`}
                variant="rounded"
                sx={classes.skeleton}
              />
            ))
        ) : orders.length === 0 ? (
          <Box sx={classes.customNoRows}>
            <CustomNoRows
              message={
                error
                  ? "Error intentando obtener las órdenes"
                  : "No hay órdenes registradas"
              }
            />
          </Box>
        ) : (
          orders.map((order) => (
            <OrderItem
              key={`order-${order.id}`}
              reference={String(order.id)}
              company={order.companyName}
              branchName={order.branchName}
              person={order.creatorUser.fullName}
              status={order.status}
              handleOpen={
                pers.includes("pages.orders")
                  ? () => navigate(`/orders/order?id=${order.id}`)
                  : null
              }
              handleSuspend={
                pers.includes("pages.orders.suspend")
                  ? () => handleSuspend(order.id)
                  : null
              }
              handlePackages={
                pers.includes("pages.packages")
                  ? () => navigate(`/orders/packages?id=${order.id}`)
                  : null
              }
              handleDispatches={
                pers.includes("pages.dispatches")
                  ? () => navigate(`/orders/dispatches?id=${order.id}`)
                  : null
              }
            />
          ))
        )}
      </Box>
      {count > 1 && (
        <Pagination
          count={count}
          defaultPage={page}
          sx={classes.pagination}
          color="primary"
          onChange={onPagination}
        />
      )}
      <OrderClient
        open={orderClient}
        handleClose={() => setOrderClient(false)}
      />
    </>
  );
};

export default Products;
